/**
 * Login-loop guard (ADO #38557).
 *
 * When MSAL cannot settle an authenticated account — e.g. Azure returns
 * AADSTS90009 on the token exchange, or `handleRedirectPromise()` keeps
 * resolving with no account — the naive boot flow re-issues `loginRedirect()`
 * on every reload, producing an infinite `/authorize` loop that never surfaces
 * an error to the user.
 *
 * This module bounds the number of *automatic* `loginRedirect()` calls per
 * browser session. The counter lives in `sessionStorage` so it persists across
 * the redirect round-trips and any auto-reload, but resets when the tab/session
 * ends. On a genuine successful login the counter is cleared, so a healthy flow
 * never trips the guard. Once the cap is hit, the boot sequence surfaces the
 * error screen (with an explicit Retry) instead of redirecting again.
 */

const STORAGE_KEY = 'celito.msal.loginAttempts';

/**
 * Maximum number of automatic `loginRedirect()` calls allowed per session
 * before the boot flow gives up and shows the error screen. `2` allows a small
 * amount of slack (e.g. one transient hiccup) while still guaranteeing the loop
 * terminates.
 */
export const MAX_LOGIN_ATTEMPTS = 2;

function getStore(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    // Access to sessionStorage can throw (privacy mode / sandboxed iframe).
    return null;
  }
}

/** Current number of automatic login redirects issued this session. */
export function getLoginAttempts(): number {
  const store = getStore();
  if (!store) return 0;
  const raw = store.getItem(STORAGE_KEY);
  const parsed = raw ? Number.parseInt(raw, 10) : 0;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/** Increment and persist the attempt counter; returns the new value. */
export function recordLoginAttempt(): number {
  const next = getLoginAttempts() + 1;
  getStore()?.setItem(STORAGE_KEY, String(next));
  return next;
}

/** Reset the counter — call after a genuine successful login / on Retry. */
export function clearLoginAttempts(): void {
  getStore()?.removeItem(STORAGE_KEY);
}

/**
 * True once the automatic-redirect budget for this session is exhausted. When
 * this returns true the caller must NOT call `loginRedirect()` again — it must
 * surface the error screen instead.
 */
export function hasExceededLoginAttempts(): boolean {
  return getLoginAttempts() >= MAX_LOGIN_ATTEMPTS;
}
